import {useQuery} from '@tanstack/react-query';
import {ScopeType, WorkstreamBasicWithSummary, WorkstreamListService} from 'client';
import {getService} from 'utilities';

import {MAX_VISIBLE_RECENTS, pickVisibleRecents} from './recentsFrecency';
import type {PinnedItem} from './WorkstreamsSearch';

export type RecentStripItem = Extract<PinnedItem, {type: 'workstream' | 'person'}>;

export const recentStripKey = (item: RecentStripItem) =>
  item.type === 'person' ? `person:${item.ldap}` : `workstream:${item.id}`;

// Stored recents are untrusted (localStorage), so check the field that keys each.
const isRecentStripItem = (item: PinnedItem | null | undefined): item is RecentStripItem =>
  (item?.type === 'workstream' && typeof item.id === 'number') ||
  (item?.type === 'person' && typeof item.ldap === 'string');

/**
 * What the /workstreams recents strip shows: the workstreams and people the
 * rail's recents list shows (see pickVisibleRecents), in its order.
 */
export const pickRecentItems = (
  items: unknown,
  now: number = Date.now(),
  max: number = MAX_VISIBLE_RECENTS
): RecentStripItem[] => {
  if (!Array.isArray(items)) return [];
  const seen = new Set<string>();
  const valid = (items as PinnedItem[]).filter((item): item is RecentStripItem => {
    if (!isRecentStripItem(item)) return false;
    const key = recentStripKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return pickVisibleRecents(valid, now, max);
};

/**
 * Status + latest update for the strip's workstreams: one list request filtered
 * to their ids (no timeframe, so older halves still resolve). Keyed by the
 * sorted ids so pin toggles reorder without a refetch.
 */
export const useRecentWorkstreamRows = (ids: number[]) => {
  const sortedIds = [...ids].sort((a, b) => a - b);
  return useQuery({
    queryKey: ['workstreams4-recent-workstreams', sortedIds],
    queryFn: async (): Promise<Map<number, WorkstreamBasicWithSummary>> => {
      const response = await getService(
        WorkstreamListService
      ).getWorkstreamsApiV1WorkstreamWorkstreamsGet(
        ScopeType.COMPANY,
        undefined,
        JSON.stringify({workstreams: sortedIds}),
        undefined,
        undefined,
        undefined,
        undefined,
        false,
        sortedIds.length
      );
      return new Map((response.workstreams ?? []).map((row) => [row.id, row]));
    },
    enabled: sortedIds.length > 0,
    staleTime: 60 * 1000,
  });
};
